import type { TradeMutationDeps, UserPortfolioPositionWire } from '@morfi/backend-sdk-v2/client';
import { applyCheckedPositionGrossNetTrade, checkedAverageEntryPriceD9 } from '@morfi/market-math';

export type ConfirmedPositionUpdater = NonNullable<
  NonNullable<TradeMutationDeps['optimisticPositions']>['upsert']
>;
export type ConfirmedPositionTrade = Parameters<ConfirmedPositionUpdater>[0];

/** Estimates only confirmed executions; the authoritative snapshot replaces this row. */
export function estimateConfirmedPosition(
  previous: UserPortfolioPositionWire | undefined,
  trade: ConfirmedPositionTrade,
): UserPortfolioPositionWire | null {
  const row = previous ?? emptyPosition(trade);
  if (row.lifecycle !== (trade.isAmm ? 'graduated' : 'trading')) return null;
  const buy = trade.tokenDeltaD6 > BigInt(0);
  if (buy && trade.walletDebitD6 === null) return null;
  const result = applyCheckedPositionGrossNetTrade(
    {
      balanceD6: BigInt(row.balanceD6),
      claimRightsBalanceD6: BigInt(row.claimRightsBalanceD6),
      remainingCostBasisGrossD6: BigInt(row.remainingCostBasisGrossD6),
      remainingCostBasisNetD6: BigInt(row.remainingCostBasisNetD6),
      realizedPnlGrossD6: BigInt(row.realizedPnlGrossD6),
      realizedPnlNetD6: BigInt(row.realizedPnlNetD6),
      realizedCostBasisGrossD6: BigInt(row.realizedCostBasisGrossD6),
      realizedCostBasisNetD6: BigInt(row.realizedCostBasisNetD6),
    },
    buy
      ? {
          direction: 'buy',
          tokenAmountD6: trade.tokenDeltaD6,
          grossQuoteInputD6: trade.grossQuoteD6,
          netWalletDebitD6: trade.walletDebitD6 ?? BigInt(0),
          isAmm: trade.isAmm,
        }
      : {
          direction: 'sell',
          tokenAmountD6: -trade.tokenDeltaD6,
          feeExcludedQuoteProceedsD6: trade.grossQuoteD6,
          netQuoteProceedsD6: trade.quoteD6,
          isAmm: trade.isAmm,
        },
  );
  if (!result.available || !Number.isSafeInteger(row.tradeCount + 1)) return null;
  const next = result.value;
  // Fee-free basis, matching the confirmed valuation's average entry.
  const average = checkedAverageEntryPriceD9(next.gross.remainingCostBasisD6, next.balanceD6);
  if (!average.available) return null;
  // Canonical P&L is the fee-free result less the conviction penalty, so this
  // trade's own penalty books immediately instead of leaving a non-leader sell
  // reading too high until the authoritative snapshot lands.
  const realizedPenaltyD6 = BigInt(row.realizedPenaltyD6) + trade.penaltyD6;
  const holding = next.balanceD6 > BigInt(0);
  return {
    ...row,
    balanceD6: next.balanceD6.toString(),
    claimRightsBalanceD6: next.claimRightsBalanceD6.toString(),
    avgEntryPriceD9: average.value.toString(),
    costBasisD6: next.gross.remainingCostBasisD6.toString(),
    remainingCostBasisGrossD6: next.gross.remainingCostBasisD6.toString(),
    remainingCostBasisNetD6: next.net.remainingCostBasisD6.toString(),
    realizedPnlGrossD6: next.gross.realizedPnlD6.toString(),
    realizedPnlNetD6: next.net.realizedPnlD6.toString(),
    realizedPenaltyD6: realizedPenaltyD6.toString(),
    realizedPnlD6: (next.gross.realizedPnlD6 - realizedPenaltyD6).toString(),
    realizedCostBasisGrossD6: next.gross.realizedCostBasisD6.toString(),
    realizedCostBasisNetD6: next.net.realizedCostBasisD6.toString(),
    realizedCostBasisD6: next.gross.realizedCostBasisD6.toString(),
    tradeCount: row.tradeCount + 1,
    isOpen: holding,
    resultState: trade.isAmm
      ? holding
        ? 'won_holding'
        : 'won_sold'
      : holding
        ? 'open'
        : 'sold_out',
  };
}

function emptyPosition(trade: ConfirmedPositionTrade): UserPortfolioPositionWire {
  return {
    positionId: `${trade.walletAddress}:${trade.market}:${trade.outcomeIndex}`,
    wallet: trade.walletAddress,
    market: trade.market,
    outcomeIndex: trade.outcomeIndex,
    outcomeMint: trade.outcomeMint,
    positionType: trade.isAmm ? 'winner' : 'outcome',
    marketStatus: trade.isAmm ? 4 : 2,
    lifecycle: trade.isAmm ? 'graduated' : 'trading',
    balanceD6: '0',
    claimRightsBalanceD6: '0',
    avgEntryPriceD9: '0',
    currentPriceD9: null,
    costBasisD6: '0',
    remainingCostBasisGrossD6: '0',
    remainingCostBasisNetD6: '0',
    realizedPnlGrossD6: '0',
    realizedPnlNetD6: '0',
    realizedPenaltyD6: '0',
    realizedPnlD6: '0',
    realizedCostBasisGrossD6: '0',
    realizedCostBasisNetD6: '0',
    realizedCostBasisD6: '0',
    unrealizedPnlD6: '0',
    tradeCount: 0,
    isOpen: false,
    resultState: 'sold_out',
    prizeClaimableD6: '0',
    prizeClaimedD6: '0',
    prizeState: 'none',
    marketTitle: null,
    outcomeLabel: null,
    outcomeTicker: '',
    outcomeImageUrl: null,
    outcomeImageNsfw: false,
    winningOutcomeIndex: trade.isAmm ? trade.outcomeIndex : null,
    poolId: null,
    coordinates: { slot: '0', txIndex: 0, cpiIndex: '0', sequence: null },
  };
}
